import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  Save, Plus, Trash2, GripVertical, RotateCcw, ClipboardList,
  Eye, EyeOff, PlaneTakeoff, ClipboardCheck, X,
} from "lucide-react";
import { Reorder, useDragControls } from "framer-motion";
import api, { formatErr } from "../lib/api";
import { useAuth } from "../lib/auth";
import { BRAND_COLORS, withAlpha } from "../lib/bookingStatus";
import { StandardServicesSection } from "../components/checklist-master/StandardServicesSection";
import { ExtrasSection } from "../components/checklist-master/ExtrasSection";
import { deDateTime } from "../lib/dateFormat";

/**
 * Feb 2026 · Admin-Master-Vorlage für die persönliche Kunden-Checkliste.
 *
 * User-Directive: „Bitte alle Checklisten in der App so anpassen, dass sie
 * vom Admin-Nutzer bearbeitet werden können (Items hinzufügen, ändern,
 * löschen, Reihenfolge anpassen)."
 *
 * Feb 2026 (Enhancement): Drag-and-Drop-Sortierung via framer-motion
 * `Reorder`, Live-Kunden-Vorschau (Side-by-Side) und „Auf Standard
 * zurücksetzen" mit Preview-Dialog (Vorher-Bestätigung).
 *
 * Diese Seite editiert die zentrale Master-Vorlage (Abreise + Anreise),
 * die als Default für alle neuen Buchungen greift, solange der Kunde
 * keine eigene Anpassung in `/settings` gemacht hat. Andere Checklisten
 * in der App sind bereits woanders editierbar:
 *   - Inklusiv-Services + Extras: `/team/design-gallery` / Server-CRUD
 *   - Live-Status-Struktur:        `/team/live-status-schema`
 */

// Fallback previews für den Reset-Dialog — spiegelt bookings_router.py
// `DEFAULT_DEPARTURE_CHECKLIST` / `DEFAULT_ARRIVAL_CHECKLIST` 1:1 wider.
// Muss synchron gehalten werden, damit der Admin *vor* dem Reset genau
// sieht, was der „Auf Standard zurücksetzen"-Button erzeugen wird.
const DEFAULT_DEPARTURE_LABELS = [
  "Alle Fenster und Türen geschlossen",
  "Alarmanlage aktiviert",
  "Müll entsorgt",
  "Kühlschrank kontrolliert · Verderbliches entfernt",
  "Wasserzufuhr abgestellt",
  "Heizung / Klima auf Abwesenheits-Modus",
  "Kaffeemaschine · Elektrogeräte ausgeschaltet",
  "Rollläden heruntergelassen",
];
const DEFAULT_ARRIVAL_LABELS = [
  "Heizung auf Wohntemperatur eingestellt",
  "Rollläden geöffnet · Räume gelüftet",
  "Frische Lebensmittel bereitgestellt",
  "Wasserzufuhr geöffnet · Boiler eingeschaltet",
  "Alarmanlage deaktiviert",
  "Blumen · Willkommensgruß platziert",
];

const uid = () => `uid-${Math.random().toString(36).slice(2, 10)}`;
// `Reorder` braucht stabile Identität pro Item. Da Backend-`key` nach dem
// Speichern eindeutig ist, aber neu eingegebene Items initial `key: ""`
// haben, hängen wir jedem Item ein Client-`_uid` an; beim Speichern wird
// es wieder entfernt (Backend generiert daraus die finalen `key`s).
const withUid = (items) => items.map((it) => ({ ...it, _uid: it._uid || uid() }));
const stripUid = (items) => items.map(({ _uid, ...rest }) => rest);

function DraggableRow({ item, kind, idx, onLabelChange, onDelete }) {
  const controls = useDragControls();
  return (
    <Reorder.Item
      value={item}
      dragListener={false}
      dragControls={controls}
      className="flex items-center gap-2 border border-navy/10 bg-white px-2 py-1.5"
      whileDrag={{
        scale: 1.02,
        boxShadow: "0 8px 24px rgba(15, 30, 61, 0.18)",
        zIndex: 5,
      }}
      data-testid={`checklist-master-${kind}-row-${idx}`}
    >
      <button
        type="button"
        // Nur der Grip-Handle startet das Ziehen — der Rest der Row bleibt
        // klickbar (Input-Fokus, Löschen). `touch-none` verhindert Scroll-
        // konflikte auf iPad/Trackpad.
        onPointerDown={(e) => { e.preventDefault(); controls.start(e); }}
        aria-label="Punkt greifen und verschieben"
        data-testid={`checklist-master-${kind}-drag-${idx}`}
        className="cursor-grab active:cursor-grabbing text-navy-dark/30 hover:text-navy/70 p-1 -ml-1 touch-none"
      >
        <GripVertical className="w-4 h-4" strokeWidth={1.6} />
      </button>
      <input
        type="text"
        value={item.label || ""}
        onChange={(e) => onLabelChange(e.target.value)}
        maxLength={180}
        data-testid={`checklist-master-${kind}-input-${idx}`}
        className="flex-1 min-w-0 bg-transparent focus:outline-none text-[13.5px] text-navy px-1"
      />
      <button
        type="button"
        onClick={onDelete}
        aria-label="Punkt löschen"
        data-testid={`checklist-master-${kind}-delete-${idx}`}
        className="text-navy-dark/40 hover:text-red-attn p-1"
      >
        <Trash2 className="w-3.5 h-3.5" strokeWidth={1.8} />
      </button>
    </Reorder.Item>
  );
}

function ChecklistEditor({ title, items, onChange, kind }) {
  const [newLabel, setNewLabel] = useState("");

  const addItem = () => {
    const label = newLabel.trim();
    if (!label) return;
    onChange([...items, { _uid: uid(), key: "", label }]);
    setNewLabel("");
  };
  const setLabel = (targetUid, label) =>
    onChange(items.map((x) => (x._uid === targetUid ? { ...x, label } : x)));
  const remove = (targetUid) =>
    onChange(items.filter((x) => x._uid !== targetUid));

  return (
    <section
      className="bg-white border border-navy/10 p-6 space-y-4"
      data-testid={`checklist-master-${kind}-card`}
    >
      <header className="flex items-center gap-3">
        <div
          className="w-10 h-10 rounded-full flex items-center justify-center"
          style={{ background: withAlpha(BRAND_COLORS.gold, 0.14), color: BRAND_COLORS.goldDark }}
        >
          <ClipboardList className="w-5 h-5" strokeWidth={1.5} />
        </div>
        <div className="min-w-0">
          <div className="font-display text-lg text-navy leading-tight">{title}</div>
          <div className="text-[12px] text-navy-dark/60">
            {items.length} Punkt{items.length === 1 ? "" : "e"} · Am Handgriff ziehen zum Sortieren
          </div>
        </div>
      </header>

      {items.length === 0 ? (
        <div
          className="text-[13px] text-navy-dark/60 italic p-3 border border-dashed border-navy/15"
          data-testid={`checklist-master-${kind}-empty`}
        >
          Noch keine Punkte — bitte unten hinzufügen.
        </div>
      ) : (
        <Reorder.Group
          axis="y"
          values={items}
          onReorder={onChange}
          className="space-y-2 list-none pl-0 m-0"
          data-testid={`checklist-master-${kind}-list`}
        >
          {items.map((it, idx) => (
            <DraggableRow
              key={it._uid}
              item={it}
              idx={idx}
              kind={kind}
              onLabelChange={(label) => setLabel(it._uid, label)}
              onDelete={() => remove(it._uid)}
            />
          ))}
        </Reorder.Group>
      )}

      <div className="flex items-center gap-2 pt-1 border-t border-navy/5">
        <input
          type="text"
          value={newLabel}
          onChange={(e) => setNewLabel(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addItem(); } }}
          placeholder="Neuen Punkt hinzufügen …"
          maxLength={180}
          data-testid={`checklist-master-${kind}-new`}
          className="flex-1 bg-white border border-navy/20 focus:border-gold focus:outline-none px-3 py-2 text-[13px]"
        />
        <button
          type="button"
          onClick={addItem}
          disabled={!newLabel.trim()}
          data-testid={`checklist-master-${kind}-add`}
          className="inline-flex items-center gap-1.5 bg-navy text-ivory hover:bg-navy-dark disabled:opacity-50 px-3 py-2 text-[11.5px] tracking-[0.14em] uppercase"
        >
          <Plus className="w-3.5 h-3.5" strokeWidth={2} />
          Hinzufügen
        </button>
      </div>
    </section>
  );
}

/**
 * Kunden-Vorschau (read-only) — spiegelt das Aussehen der `ChecklistSection`
 * aus `BookingDetailModal` (Progress-Bar 0/N, Checkbox-Icons, Gold-Header).
 * Live-Update: Punkte, die im Editor tickern, tauchen sofort hier auf.
 */
function CustomerPreview({ title, icon: Icon, items, kind }) {
  const total = items.length;
  return (
    <section
      className="bg-ivory/60 border border-dashed border-gold/40 p-5 space-y-3"
      data-testid={`checklist-master-${kind}-preview`}
    >
      <div className="flex items-center gap-2">
        <Eye className="w-3.5 h-3.5 text-navy-dark/50" strokeWidth={1.6} />
        <div className="label-caps text-navy-dark/60">Kunden-Vorschau · so sieht der Kunde die Liste</div>
      </div>
      <div className="bg-white border border-navy/10 p-5">
        <div className="flex items-baseline justify-between mb-3">
          <div className="flex items-center gap-2">
            <Icon className="w-4 h-4" style={{ color: BRAND_COLORS.gold }} strokeWidth={1.6} />
            <div className="label-caps" style={{ color: BRAND_COLORS.gold }}>{title}</div>
          </div>
          <div className="label-caps text-navy-dark/60">0 / {total}</div>
        </div>
        <div
          className="h-1 w-full rounded-full overflow-hidden mb-3"
          style={{ background: withAlpha(BRAND_COLORS.navy, 0.08) }}
        >
          <div className="h-full" style={{ width: "0%", background: BRAND_COLORS.gold }} />
        </div>
        {total === 0 ? (
          <div className="text-[12.5px] text-navy-dark/50 italic py-2">
            (leer — bitte links Punkte hinzufügen)
          </div>
        ) : (
          <ul className="space-y-1.5 list-none pl-0 m-0">
            {items.map((it, i) => {
              const label = (it.label || "").trim();
              return (
                <li
                  key={it._uid || `${kind}-prev-${i}`}
                  data-testid={`checklist-master-${kind}-preview-item-${i}`}
                  className="w-full flex items-start gap-2 text-[13px] leading-snug"
                >
                  <span
                    className="mt-0.5 w-4 h-4 border rounded flex items-center justify-center shrink-0"
                    style={{ background: "#fff", borderColor: withAlpha(BRAND_COLORS.navy, 0.30) }}
                    aria-hidden
                  />
                  <span className="flex-1 min-w-0 text-navy-dark">
                    {label || <em className="text-navy-dark/40">(ohne Beschriftung)</em>}
                  </span>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </section>
  );
}

/**
 * Reset-Bestätigungs-Dialog: Bevor die aktuelle Master-Vorlage überschrieben
 * wird, sieht der Admin genau, welche Standard-Punkte danach greifen —
 * side-by-side Abreise + Anreise. Kein Reset ohne Bestätigung.
 */
function ResetPreviewDialog({ open, saving, onConfirm, onCancel }) {
  if (!open) return null;
  return (
    <div
      className="fixed inset-0 z-50 flex items-end md:items-center justify-center p-0 md:p-4"
      style={{ background: `${withAlpha(BRAND_COLORS.navyDark, 0.55)}` }}
      onClick={(e) => { if (e.target === e.currentTarget && !saving) onCancel(); }}
      data-testid="checklist-master-reset-dialog"
    >
      <div className="bg-ivory w-full max-w-3xl max-h-[92vh] overflow-y-auto border border-gold/40">
        <div className="sticky top-0 z-10 bg-ivory border-b border-navy/10 px-5 md:px-7 py-4 flex items-start justify-between gap-4">
          <div className="min-w-0">
            <div className="label-caps text-navy-dark/60 mb-1">Sicherheitsabfrage</div>
            <div className="font-display text-xl md:text-2xl text-navy leading-tight">
              Auf Weissblom-Standard zurücksetzen?
            </div>
            <p className="text-[12.5px] text-navy-dark/70 mt-2 max-w-xl leading-relaxed">
              Ihre aktuelle Master-Vorlage wird durch die untenstehenden
              Standard-Punkte ersetzt. Bestehende Kunden-Anpassungen unter{" "}
              <em>Einstellungen &rarr; Meine Checkliste</em> bleiben erhalten.
            </p>
          </div>
          <button
            type="button"
            onClick={onCancel}
            disabled={saving}
            className="w-8 h-8 border border-navy/15 hover:border-navy flex items-center justify-center disabled:opacity-40"
            data-testid="checklist-master-reset-cancel-icon"
            aria-label="Abbrechen"
          >
            <X className="w-4 h-4 text-navy" strokeWidth={1.6} />
          </button>
        </div>

        <div className="px-5 md:px-7 py-6 grid grid-cols-1 md:grid-cols-2 gap-5">
          <div className="bg-white border border-navy/10 p-4">
            <div className="flex items-center gap-2 mb-3">
              <PlaneTakeoff className="w-4 h-4" style={{ color: BRAND_COLORS.gold }} strokeWidth={1.6} />
              <div className="label-caps" style={{ color: BRAND_COLORS.gold }}>
                Abreise · {DEFAULT_DEPARTURE_LABELS.length} Punkte
              </div>
            </div>
            <ul
              className="space-y-1.5 list-none pl-0 m-0"
              data-testid="checklist-master-reset-preview-departure"
            >
              {DEFAULT_DEPARTURE_LABELS.map((label) => (
                <li key={label} className="flex items-start gap-2 text-[13px] leading-snug">
                  <span className="w-1 h-1 rounded-full mt-2 shrink-0" style={{ background: BRAND_COLORS.gold }} />
                  <span className="text-navy-dark">{label}</span>
                </li>
              ))}
            </ul>
          </div>
          <div className="bg-white border border-navy/10 p-4">
            <div className="flex items-center gap-2 mb-3">
              <ClipboardCheck className="w-4 h-4" style={{ color: BRAND_COLORS.gold }} strokeWidth={1.6} />
              <div className="label-caps" style={{ color: BRAND_COLORS.gold }}>
                Anreise · {DEFAULT_ARRIVAL_LABELS.length} Punkte
              </div>
            </div>
            <ul
              className="space-y-1.5 list-none pl-0 m-0"
              data-testid="checklist-master-reset-preview-arrival"
            >
              {DEFAULT_ARRIVAL_LABELS.map((label) => (
                <li key={label} className="flex items-start gap-2 text-[13px] leading-snug">
                  <span className="w-1 h-1 rounded-full mt-2 shrink-0" style={{ background: BRAND_COLORS.gold }} />
                  <span className="text-navy-dark">{label}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>

        <div className="sticky bottom-0 bg-ivory border-t border-navy/10 px-5 md:px-7 py-4 flex items-center justify-end gap-3">
          <button
            type="button"
            onClick={onCancel}
            disabled={saving}
            data-testid="checklist-master-reset-cancel"
            className="text-[11.5px] tracking-[0.14em] uppercase text-navy-dark/70 hover:text-navy px-4 py-2 border border-navy/15 hover:border-navy/40 disabled:opacity-50"
          >
            Abbrechen
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={saving}
            data-testid="checklist-master-reset-confirm"
            className="inline-flex items-center gap-2 bg-navy text-ivory hover:bg-navy-dark disabled:opacity-60 disabled:cursor-not-allowed px-5 py-2.5 text-[11.5px] tracking-[0.14em] uppercase"
          >
            <RotateCcw className="w-3.5 h-3.5" strokeWidth={1.7} />
            {saving ? "Setze zurück …" : "Auf Standard zurücksetzen"}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function TeamChecklistMaster() {
  const { user } = useAuth();
  const [departure, setDeparture] = useState([]);
  const [arrival, setArrival] = useState([]);
  const [meta, setMeta] = useState({ updated_at: null, updated_by: null });
  const [saving, setSaving] = useState(false);
  const [resetOpen, setResetOpen] = useState(false);
  const [resetBusy, setResetBusy] = useState(false);
  const [showPreview, setShowPreview] = useState(true);
  const [initial, setInitial] = useState({ departure: [], arrival: [] });

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/team/settings/checklist-master");
        const dep = withUid(data.departure || []);
        const arr = withUid(data.arrival || []);
        setDeparture(dep);
        setArrival(arr);
        setInitial({ departure: dep, arrival: arr });
        setMeta({ updated_at: data.updated_at, updated_by: data.updated_by });
      } catch (e) {
        toast.error(formatErr(e?.response?.data?.detail));
      }
    })();
  }, []);

  const dirty = useMemo(
    () => JSON.stringify(stripUid(initial.departure)) !== JSON.stringify(stripUid(departure))
       || JSON.stringify(stripUid(initial.arrival)) !== JSON.stringify(stripUid(arrival)),
    [initial, departure, arrival],
  );

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/team/settings/checklist-master", {
        departure: stripUid(departure),
        arrival: stripUid(arrival),
      });
      const dep = withUid(data.departure || []);
      const arr = withUid(data.arrival || []);
      setDeparture(dep);
      setArrival(arr);
      setInitial({ departure: dep, arrival: arr });
      setMeta({ updated_at: data.updated_at, updated_by: data.updated_by });
      toast.success("Master-Vorlage gespeichert · gilt für alle neuen Buchungen.");
    } catch (e) {
      toast.error(formatErr(e?.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const doReset = async () => {
    setResetBusy(true);
    try {
      const { data } = await api.post("/team/settings/checklist-master/reset");
      const dep = withUid(data.departure || []);
      const arr = withUid(data.arrival || []);
      setDeparture(dep);
      setArrival(arr);
      setInitial({ departure: dep, arrival: arr });
      setMeta({ updated_at: data.updated_at, updated_by: data.updated_by });
      toast.success("Checkliste auf Weissblom-Standard zurückgesetzt.");
      setResetOpen(false);
    } catch (e) {
      toast.error(formatErr(e?.response?.data?.detail));
    } finally {
      setResetBusy(false);
    }
  };

  if (user?.role !== "admin") {
    return (
      <div className="p-8" data-testid="checklist-master-forbidden">
        <div className="max-w-2xl bg-white border-l-4 border-red-500 p-6">
          <div className="label-caps text-red-700 mb-1">Kein Zugriff</div>
          <div className="text-navy-dark">Diese Einstellungen sind Admins vorbehalten.</div>
        </div>
      </div>
    );
  }

  return (
    <div
      className="p-6 md:p-10 max-w-[1400px] mx-auto space-y-8"
      data-testid="checklist-master-page"
    >
      <header className="flex items-start justify-between gap-4 flex-wrap">
        <div className="space-y-2 max-w-2xl">
          <div className="label-caps" style={{ color: BRAND_COLORS.gold }}>Einstellungen · Checklisten</div>
          <h1 className="font-display text-3xl text-navy">Persönliche Kunden-Checkliste</h1>
          <p className="text-[14px] text-navy-dark/70 leading-relaxed">
            Diese Master-Vorlage ist der Ausgangspunkt der Abreise- und Anreise-
            Checkliste jedes Kunden. Ziehen Sie Punkte am Handgriff, um sie neu
            zu sortieren — die Änderung greift für alle neu angelegten
            Buchungen. Kunden, die ihre eigene Liste in{" "}
            <em>Einstellungen &rarr; Meine Checkliste</em> gepflegt haben,
            behalten ihre Anpassung.
          </p>
          {meta.updated_at && (
            <div className="text-[11.5px] text-navy-dark/50">
              Zuletzt bearbeitet: {deDateTime(meta.updated_at)}
            </div>
          )}
        </div>
        <button
          type="button"
          onClick={() => setShowPreview((v) => !v)}
          data-testid="checklist-master-toggle-preview"
          className="inline-flex items-center gap-2 text-[11px] tracking-[0.15em] uppercase text-navy-dark/70 hover:text-navy px-3 py-2 border border-navy/15 hover:border-navy/40 transition-colors"
        >
          {showPreview ? (
            <>
              <EyeOff className="w-3.5 h-3.5" strokeWidth={1.6} />
              Vorschau ausblenden
            </>
          ) : (
            <>
              <Eye className="w-3.5 h-3.5" strokeWidth={1.6} />
              Vorschau einblenden
            </>
          )}
        </button>
      </header>

      {/* Abreise-Zeile: Editor + Kunden-Vorschau (Side-by-Side) */}
      <div className={`grid grid-cols-1 ${showPreview ? "lg:grid-cols-2" : ""} gap-6`}>
        <ChecklistEditor
          title="Abreise-Checkliste"
          items={departure}
          onChange={setDeparture}
          kind="departure"
        />
        {showPreview && (
          <CustomerPreview
            title="Abreise-Checkliste"
            icon={PlaneTakeoff}
            items={departure}
            kind="departure"
          />
        )}
      </div>

      {/* Anreise-Zeile: Editor + Kunden-Vorschau (Side-by-Side) */}
      <div className={`grid grid-cols-1 ${showPreview ? "lg:grid-cols-2" : ""} gap-6`}>
        <ChecklistEditor
          title="Anreise-Checkliste"
          items={arrival}
          onChange={setArrival}
          kind="arrival"
        />
        {showPreview && (
          <CustomerPreview
            title="Anreise-Checkliste"
            icon={ClipboardCheck}
            items={arrival}
            kind="arrival"
          />
        )}
      </div>

      {/* Feb 2026 · Vorbereitung — Inklusiv-Leistungen (voller CRUD).
          Diese Liste treibt die „Vorbereitung"-Phase des Booking-
          Timelines: jede Buchung erbt automatisch alle nicht-abgewählten
          Punkte. Master-Prinzip: neue Einträge greifen sofort für alle
          Buchungen, Löschungen entfernen nur die Sichtbarkeit — bestehende
          Team-Toggles bleiben unversehrt. */}
      <StandardServicesSection />

      {/* Feb 2026 · Zusatzleistungen (Extras · voller CRUD).
          Kunden buchen sie einzeln im Ab-/Anreise-Wizard. Der Editor
          erlaubt das komplette Anlegen, Umbenennen, Kategorisieren und
          Löschen aus einer Master-Liste heraus. */}
      <ExtrasSection />

      {/* Sticky Action-Bar: Reset (links) + Speichern (rechts) */}
      <div
        className="flex items-center justify-between gap-3 flex-wrap sticky bottom-4 bg-ivory/95 backdrop-blur border border-navy/10 px-4 py-3"
        style={{ boxShadow: "0 6px 24px rgba(15, 30, 61, 0.10)" }}
      >
        <button
          type="button"
          onClick={() => setResetOpen(true)}
          disabled={saving || resetBusy}
          data-testid="checklist-master-reset"
          className="inline-flex items-center gap-2 text-[11px] tracking-[0.15em] uppercase text-navy-dark/70 hover:text-navy px-3 py-2 border border-navy/15 hover:border-navy/40 transition-colors disabled:opacity-40"
        >
          <RotateCcw className="w-3.5 h-3.5" strokeWidth={1.6} />
          Auf Standard zurücksetzen
        </button>

        <div className="flex items-center gap-3">
          {dirty && (
            <span className="text-[11.5px] text-navy-dark/70 italic">
              Ungespeicherte Änderungen
            </span>
          )}
          <button
            type="button"
            onClick={save}
            disabled={saving || !dirty}
            data-testid="checklist-master-save"
            className="inline-flex items-center gap-2 bg-navy text-ivory hover:bg-navy-dark disabled:opacity-60 disabled:cursor-not-allowed px-6 py-2.5 text-[12px] tracking-[0.15em] uppercase"
          >
            <Save className="w-4 h-4" strokeWidth={1.7} />
            {saving ? "Speichern …" : "Master-Vorlage speichern"}
          </button>
        </div>
      </div>

      <ResetPreviewDialog
        open={resetOpen}
        saving={resetBusy}
        onCancel={() => { if (!resetBusy) setResetOpen(false); }}
        onConfirm={doReset}
      />
    </div>
  );
}
